import {
    IAzureDevOpsParsedFileInfo,
    ICodeEditorExtensionPoints, IContributedLanguageProvider, IContribution, IFileToolTipProvider, IJsonSchemaRegistration, ILanguageRegistration,
    IResource, IResourceService, IResourceVersion, ResourceChangedCallback, VersionControlType
} from "BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor.Contributions";
import { MonacoProviderProxy } from "BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor.ExternalNavigation";
import * as Contributions_Controls from "VSS/Contributions/Controls";
import * as Contributions_Services from "VSS/Contributions/Services";
import * as VSS_Error from "VSS/Error";
import { getLWPService } from "VSS/LWP";
import * as SDK_Shim from "VSS/SDK/Shim";
import * as VSS_Service from "VSS/Service";
import * as Q from "q";
import { CodeEditor } from "./TFS.Extensions.CodeEditor";

/** Convert this object to RegExp if it appears to be a proxied RegExp.
    Returns null if cannot convert. */
function tryConvertToRegExp(obj: any) {
    // Don't convert existing RegExps, which can occur for internal extensions or if the proxying layer adds first-class support for RegExps.
    if (obj instanceof RegExp) {
        return obj;
    }
    if (obj.exec instanceof Function
        && obj.test instanceof Function
        && (typeof obj.flags === "string" || obj.flags === undefined) //Edge and IE11 use undefined if not specified
        && (typeof obj.source === "string")) {
        try {
            return new RegExp(obj.source, obj.flags);
        }
        catch (error) {
            return null;
        }
    }
    return null;
}

/** Convert this object and any nested objects to RegExp if they appear to be a proxied RegExp. */
function convertRegExpProxies(obj: any) {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Array) {
        return obj.map(convertRegExpProxies);
    }
    else if (typeof obj === "object") {
        var regExp = tryConvertToRegExp(obj);
        if (regExp) {
            return regExp;
        }
        var result = {};
        var keys = getAllPropertyNames(obj);
        for (var key in keys) {
            var value = obj[key];
            if ((key && key[0] !== "_")) {
                result[key] = convertRegExpProxies(value);
            }
            else {
                result[key] = value;
            }
        }
        return result;
    }
    return obj;
}

/**
    * Gets all own and inherited property names of the given object, excluding
    * those that are inherited from Object's prototype and "constructor".
    * @param obj
    */
function getAllPropertyNames(obj: any) {
    const properties: { [key: string]: true } = {};
    while (obj && obj !== Object.prototype) {
        const ownPropertyNames = Object.getOwnPropertyNames(obj);
        for (const name of ownPropertyNames) {
            if (name !== "constructor") {
                properties[name] = true;
            }
        }
        obj = Object.getPrototypeOf(obj);
    }

    return properties;
}



function getJsonSchemas(): IPromise<IJsonSchemaRegistration[]> {
    var originalSchemas = monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas;
    return Q(originalSchemas.map(original => Object.assign({}, original)));
}

const resourceChangedCallbacks: ResourceChangedCallback[] = [];

/**
 * Register a callback for when a resource changes.
 * @param callback
 */
function onResourceChanged(callback: ResourceChangedCallback) {
    if (callback) {
        resourceChangedCallbacks.push(callback);
        /* Let the callback know about any pre-existing models */
        for (var model of monaco.editor.getModels()) {
            sendResourceUpdatedMessage(model, callback);
        }
    }
}

/** Send a message to all callbacks that the resource has updated. */
function resourceUpdated(model: monaco.editor.ITextModel) {
    resourceChangedCallbacks.map(callback => sendResourceUpdatedMessage(model, callback));
}

/** Send a message to a callback that the resource has updated. */
function sendResourceUpdatedMessage(model: monaco.editor.ITextModel, callback: ResourceChangedCallback) {
    getResourceInfo(model).then(resource => {
        if (resource) {
            try {
                callback(resource);
            }
            catch (error) {
                handleError(`ResourceChanged callback failed for '${resource.uri || "<unidentified resource>"}'`, error);;
            }
        }
    });
}

function getResourceInfo(model: monaco.editor.ITextModel): IPromise<IResource> {
    var uri = model.uri.toString();

    return getResourceVersion(uri).then(versionInfo => {
        if (versionInfo) {
            const getValue = () => Q(model.getValue());
            const languageId = model.getModeId();

            return {
                getValue,
                languageId,
                uri,
                versionInfo
            }
        }
        return null;
    });
}

/**
 * Register the supplied schemas, overwriting any with matching URIs but keeping everything else.
 * @param schemas Schemas to register
 */
function registerJsonSchemas(schemas: IJsonSchemaRegistration[]) {
    var options = monaco.languages.json.jsonDefaults.diagnosticsOptions;
    var updatedSchemas = options.schemas.filter(s1 => !schemas.find(s2 => s1.uri === s2.uri));
    for (var schema of schemas) {
        updatedSchemas.push(schema);
    }
    var updatedOptions = Object.assign({}, options);
    Object.assign(updatedOptions, { schemas: updatedSchemas });
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(updatedOptions);
}

/** Register a new language with monaco */
function registerLanguage(registration: ILanguageRegistration) {
    var id = registration.extensionPoint && registration.extensionPoint.id;
    if (!id) {
        return;
    }
    registration = convertRegExpProxies(registration);
    try {
        monaco.languages.register({
            ...registration.extensionPoint,
            configuration: registration.extensionPoint.configuration ? monaco.Uri.parse(registration.extensionPoint.configuration) : undefined
        });
        monaco.languages.onLanguage(registration.extensionPoint.id, () => {
            try {
                if (registration.configuration) {
                    monaco.languages.setLanguageConfiguration(id, registration.configuration);
                }
            }
            catch (error) {
                handleError(`Failed to setLanguageConfiguration for '${id}'`, error);
            }
            try {
                if (registration.monarchLanguage) {
                    monaco.languages.setMonarchTokensProvider(id, registration.monarchLanguage);
                }
            }
            catch (error) {
                handleError(`Failed to setMonarchTokensProvider for '${id}'`, error);
            }
        });
    }
    catch (error) {
        handleError(`Failed to registerLanguage for '${id}'`, error);
    }
    updateLanguagesForExistingModels();
}

let disposables: IDisposable[] = [];
let _firstUrl: monaco.Uri;
async function registerNavigationProvider(contribution: IContributedLanguageProvider, editor: CodeEditor, monacoEditor: monaco.editor.ICodeEditor, fileUrl?: monaco.Uri) {
    // if it's search view, we would want to remove any file tool tip
    const searchView: boolean = top?.document.getElementsByClassName('search-view-container') !== null;
    const searchViewTitleClass = "fileName-header";
    const fileToolTipButtonClass = `${searchViewTitleClass} richnav button`;

    // For non-indexed files the tool tip should be cleaned up everytime the editor is loaded
    cleanUpCurrentSearchViewToolTip(fileToolTipButtonClass);

    if (disposables && disposables.length > 0) {
        for (let disposable of disposables) {
            disposable.dispose();
        }
        disposables.length = 0;
    }

    if (!contribution) {
        // if there is no language provider extension present, do not proceed
        return;
    }

    if (fileUrl || _firstUrl) {
        // use initial values or values from a change in file view
        fileUrl = fileUrl ?? _firstUrl;
        editor = editor ?? _initialEditor;
        monacoEditor = monacoEditor ?? _initialMonacoEditor;

        const parsedFile: IAzureDevOpsParsedFileInfo | null = tryParseFileUrl(fileUrl);
        if (!parsedFile) {
            return;
        }

        const monacoProvider = new MonacoProviderProxy(contribution.providers, parsedFile.repoId, editor.getEditorThemeName());

        // assumption: only commit ids will be passed from search view
        if (parsedFile.isCommit &&
            MonacoProviderProxy.isKnownLanguage(contribution.availableLanguages, parsedFile.fileExtension)) {
            const capabilities = await contribution.getFileCapabilities(parsedFile.relativeFilePath, parsedFile.commitId, parsedFile.isCommit);

            if (!capabilities) {
                // if no capabilities are returned then don't make any other extension calls
                return;
            }

            const codeLensContext = contribution.getCodeLensContext ? contribution.getCodeLensContext() : undefined;
            const identifiersPromise: Promise<monaco.IRange[]> = contribution.getKnownIdentifiers(
                parsedFile.relativeFilePath,
                parsedFile.commitId,
                parsedFile.isCommit);

            monacoProvider.registerProviders(parsedFile.fileExtension, capabilities, codeLensContext, disposables);
            monacoProvider.addDiscoverability(identifiersPromise, editor, monacoEditor);
            editor.setNavContextMenu(contribution.contextMenuItems, monacoEditor);

            // Since the editor contribution loads on demand, document needs to be modified under search view
            // checks if it's in search view
            if (searchView) {
                // Since the editor contribution loads on demand we would want to clean up
                // every time the editor is loaded
                cleanUpCurrentSearchViewToolTip(fileToolTipButtonClass);

                // check if the file is indexed
                const toolTipResponse: IFileToolTipProvider = await contribution?.getToolTipProvider(
                    parsedFile.relativeFilePath,
                    parsedFile.commitId,
                    parsedFile.isCommit);

                const title = top?.document.getElementsByClassName(searchViewTitleClass).item(0);
                const button = createToolTipButton(fileToolTipButtonClass, toolTipResponse);
                title?.appendChild(button);
            }
        }
        else {
            // cleanup any highlighting on files not supported with rich navigation
            monacoProvider.removeHighlighing(editor, monacoEditor);
        }
    }
}

// since OfficeFabric button can not be imported into the project for legacy dependencies we have to manually create a tooltip
const createToolTipButton = (buttonClassName: string, toolTipResponse: IFileToolTipProvider): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = buttonClassName;
    button.title = toolTipResponse.buttonText;
    button.innerHTML = `<span class="fabric-icon ms-Icon--${toolTipResponse.buttonIcon} medium"></span>`
    button.style.background = 'none';
    button.style.color = toolTipResponse.buttonColor;
    button.style.border = 'none';
    button.onclick = (_ev) => { window.open(toolTipResponse.onClickRef); }
    return button;
}

// utility method to clean up any file tool tip if it already exists
const cleanUpCurrentSearchViewToolTip = (fileToolTipButtonClass: string) => {
    const checkButton: HTMLCollectionOf<Element> = top?.document.getElementsByClassName(fileToolTipButtonClass);
    // HTMLCollection is not an instance of array/collection hence array methods won't work
    for (let i = checkButton.length - 1; i >= 0; --i) {
        checkButton[i].remove();
    }
}

// utility method to parse a file Url to an Azure DevOps file
const tryParseFileUrl = (fileUrl: monaco.Uri): IAzureDevOpsParsedFileInfo | null => {
    try {
        // deconstruct fileUrl for lookup values
        // since this will be in search view - it will be a commit
        // example fileUrl = "inmemory://model/git/<some-git-value?/GC<commit>/src/<path>"
        const pathComponents = fileUrl.path.split("/");
        const repoId = pathComponents[2];
        const ref = pathComponents[3];
        const isCommit = ref[1] == "C";
        const commitId = ref.slice(2);
        const filePath = pathComponents.slice(4).join("/");
        const fileExtension = filePath.substring(filePath.lastIndexOf('.') + 1);

        return {
            repoId: repoId,
            ref: ref,
            isCommit: isCommit,
            relativeFilePath: filePath,
            commitId: commitId,
            fileExtension: fileExtension
        }
    }
    catch (err) {
        return null;
    }
}

/**
* User-defined type guards for contributions that need to be reloaded
*/
function isNavigationProviderContribution(contribution: IContribution): contribution is IContributedLanguageProvider {
    return (contribution as IContributedLanguageProvider).providers !== undefined;
}

function reloadContributions(fileUrl: monaco.Uri, editor: CodeEditor, monacoEditor: monaco.editor.ICodeEditor) {
    if (_contributionsRequiringReload.length > 0) {
        for (const contribution of _contributionsRequiringReload) {
            if (isNavigationProviderContribution(contribution)) {
                registerNavigationProvider(contribution, editor, monacoEditor, fileUrl);
            }
        }
    }
    else {
        _firstUrl = fileUrl;
    }
}

let _endpointsPromise: IPromise<ICodeEditorExtensionPoints>
function getExtensionPoint() {
    if (!_endpointsPromise) {
        const deferred = Q.defer<ICodeEditorExtensionPoints>();
        _endpointsPromise = deferred.promise;

        const contributionService = getLWPService("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-features.editor").then(() => {

            monaco.editor.onDidChangeModelLanguage(({ model }) => resourceUpdated(model));
            monaco.editor.onDidCreateModel((model) => {
                resourceUpdated(model);
                model.onDidChangeContent(() => resourceUpdated(model));
            });

            deferred.resolve({
                getResourceUri,
                getResourceVersion,
                getResourceUriFromResourceVersion,
                getJsonSchemas,
                onResourceChanged,
                registerJsonSchemas,
                registerLanguage,
                registerNavigationProvider,
            });
        });
    }
    return _endpointsPromise;
}

// Lazy-load resource service because it has dependencies on VersionControl which could bring in an extra bundle.
let _resourceServicePromise: IPromise<IResourceService>;
export function getResourceService(): IPromise<IResourceService> {
    if (!_resourceServicePromise) {
        _resourceServicePromise = SDK_Shim.VSS.getService<IResourceService>("ms.vss-code-web.code-editor-resource-service");
    }
    return _resourceServicePromise;
}

function getResourceUriFromResourceVersion(version: IResourceVersion): IPromise<string> {
    return getResourceService()
        .then(service => service.getResourceUriFromResourceVersion(version));
}

function getResourceUri(repositoryId: string, version: string, path: string, versionControlType?: VersionControlType): IPromise<string> {
    return getResourceService()
        .then(service => service.getResourceUri(repositoryId, version, path, versionControlType));
}

function getResourceVersion(uri: string): IPromise<IResourceVersion> {
    return getResourceService()
        .then(service => service.getResourceVersion(uri));
}

function handleError(messagePrefix: string, error: Error) {
    error.message = `${messagePrefix}: ${error.message || ""}`;
    console.error(error);
    VSS_Error.publishErrorToTelemetry(error);
}

function updateLanguagesForExistingModels() {
    monaco.editor.getModels().forEach(model => {
        var oldUri = model.uri;
        var newUri;
        try {
            if (typeof oldUri === "string") {
                oldUri = monaco.Uri.parse(oldUri);
            }
            if (monaco.Uri.isUri(oldUri)) {
                newUri = oldUri.with({ scheme: "languagedetection" });
            }
        }
        catch (error) { }

        if (!monaco.Uri.isUri(newUri)) {
            newUri = "languagedetection:" + (oldUri || "").toString();
        }

        // Some languages can be detected by content of the first line, such as xml and files with unix shebangs
        var firstLine = model.getValueInRange({
            startColumn: 1,
            startLineNumber: 1,
            endColumn: 1,
            endLineNumber: 2
        });

        var languageDetectionModel = monaco.editor.createModel(firstLine, null, newUri);
        var newMode = languageDetectionModel.getModeId();
        if (newMode !== "plaintext" && newMode !== model.getModeId()) {
            monaco.editor.setModelLanguage(model, newMode);
        }
        languageDetectionModel.dispose();
    });
}

let _contributionsPromise: IPromise<any>;
let _initialEditor: CodeEditor;
let _initialMonacoEditor: monaco.editor.ICodeEditor;
/** Load all extensions for code editor, then register them. */
function loadContributions(editor?: CodeEditor, monacoEditor?: monaco.editor.ICodeEditor): IPromise<any> {
    _initialEditor = editor;
    _initialMonacoEditor = monacoEditor;
    _contributionsPromise = _contributionsPromise ||
        getExtensionPoint().then(endpoints =>
            VSS_Service.getService(Contributions_Services.ExtensionService)
                .getContributionsForTarget(
                    "ms.vss-code-web.code-editor-contribution-collection",
                    "ms.vss-code-web.code-editor-contribution"
                ).then(contributions => {
                    const contributionLoadingPromises = [];
                    for (const contribution of contributions) {
                        const promise = loadAndRegisterContribution(endpoints, contribution);
                        contributionLoadingPromises.push(promise);
                    }
                    return Q.all(contributionLoadingPromises);
                }));
    return _contributionsPromise;
}

let _contributionsRequiringReload: IContribution[] = [];
function loadAndRegisterContribution(endpoints: ICodeEditorExtensionPoints, contribution: Contribution) {
    const id = contribution.properties["registeredObjectId"] || contribution.id;
    return Contributions_Controls.getBackgroundInstance<IContribution>(contribution, id)
        .then(contributionInstance => {
            if (contributionInstance.register) {
                contributionInstance.register(endpoints);
                if (contributionInstance.requiresReload) {
                    _contributionsRequiringReload.push(contributionInstance);
                }
            }
        });
}

/**
    Provides contributions to the Code Editor
 */
export class ContributionLoader {
    /**
     * Load contributions for the Code Editor.
     * @returns a promise indicating when the contributions have been loaded.
     */
    public loadContributions = loadContributions;

    public reloadContributions = reloadContributions;
}